// steps/tabs/PaymentDetailsTab.tsx
import {
  useOutstandingAccounts,
  usePaymentDetails,
} from "@features/bx/BulkBillGeneration/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { TableColumn } from "@shared/types/table";
import { Switch } from "@shared/ui/switch";
import { MODAL_PAGE_SIZE } from "@shared/utils/constants";
import {
  AlertCircle,
  Calendar,
  CreditCard,
  DollarSign,
  TrendingUp,
  Users,
  FileText,
  CheckCircle,
  Wallet
} from "lucide-react";
import { useCurrency } from "@shared/selectors/globalSelectors";
import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";

interface PaymentDetailsTabProps {
  applyCredits: boolean;
  includeLateFees: boolean;
  carryforwardOutstanding: boolean;
  includeInstallments: boolean;
  setApplyCredits: (value: boolean) => void;
  setIncludeLateFees: (value: boolean) => void;
  setCarryforwardOutstanding: (value: boolean) => void;
  setIncludeInstallments: (value: boolean) => void;
}

const PaymentDetailsTab: React.FC<PaymentDetailsTabProps> = ({
  applyCredits,
  includeLateFees,
  carryforwardOutstanding,
  includeInstallments,
  setApplyCredits,
  setCarryforwardOutstanding,
  setIncludeInstallments,
  setIncludeLateFees,
}) => {
  const [page, setPage] = useState(1);
  const currency = useCurrency();
  const { id, cycleId } = useParams();
  const { data: paymentData } = usePaymentDetails(cycleId);
  const pageSize = MODAL_PAGE_SIZE;
  // Fetch top outstanding accounts with pagination
  const { data: outstandingAccountsData } = useOutstandingAccounts(
    cycleId,
    page,
    pageSize,
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const billingOptions = [
    {
      key: "credits",
      bg: "bg-blue-50 border-blue-200",
      iconBg: "bg-blue-100",
      icon: <CreditCard className="w-5 h-5 text-blue-600" />,
      title: "Apply Credits to Current Bills",
      description: "Automatically adjust bills with pending credits",
      checked: applyCredits,
      onChange: setApplyCredits,

      inactiveMessage: {
        title: "Credits Not Applied",
        description:
          "Credits will not be applied to this billing run. Consumers will receive bills without adjusting their outstanding credits.",
      },

      stats: [
        {
          label: "Consumers with credit available",
          value: paymentData?.applyCredits?.consumersWithCredit ?? "NA",
          subText: `${paymentData?.applyCredits?.totalConsumer ?? "NA"}% of total consumers`,
        },
        {
          label: "Total credit amount",
          value: paymentData?.applyCredits?.totalCreditAmount ?? "NA",
          subText: `Average: ${currency} ${paymentData?.applyCredits?.averageAmount ?? 'NA'} per consumer`,
        },
      ],
    },

    {
      key: "lateFees",
      bg: "bg-orange-50 border-orange-200",
      iconBg: "bg-orange-100",
      icon: <AlertCircle className="w-5 h-5 text-orange-600" />,
      title: "Include Late Fees in Billing",
      description: "Apply late payment charges to current bills",
      checked: includeLateFees,
      onChange: setIncludeLateFees,

      inactiveMessage: {
        title: "Late Fees Not Applied",
        description:
          "Late fees will not be included in this billing run. Consumers will receive bills without late payment charges.",
      },

      stats: [
        {
          label: "Consumers with late fees",
          value: paymentData?.lateFees?.consumersWithLateFees ?? "NA",
          subText: `${paymentData?.lateFees?.totalConsumer ?? 'NA'}% of total consumers`,
        },
        {
          label: "Total late fees",
          value: paymentData?.lateFees?.totalLateFees ?? "NA",
          subText: `Average: ${currency} ${paymentData?.lateFees?.averageAmount ?? 'NA'} per consumer`,
        },
      ],
    },

    {
      key: "outstanding",
      bg: "bg-blue-50 border-blue-200",
      iconBg: "bg-blue-100",
      icon: <DollarSign className="w-5 h-5 text-blue-600" />,
      title: "Outstandings Carryforward",
      description: "Carry forward outstanding balances to current bills",
      checked: carryforwardOutstanding,
      onChange: setCarryforwardOutstanding,

      inactiveMessage: {
        title: "Outstanding Balances Not Carried Forward",
        description:
          "Outstanding balances will not be included in this billing run. Consumers will receive bills without previous outstanding amounts.",
      },

      stats: [
        {
          label: "Consumers with outstanding",
          value:
            paymentData?.outstandingsCarryforward?.consumersWithOutstanding ??
            "NA",
          subText: `${paymentData?.outstandingsCarryforward?.totalConsumer ?? 0}% of total consumers`,
        },
        {
          label: "Total outstanding",
          value:
            paymentData?.outstandingsCarryforward?.totalOutstanding ?? "NA",
          subText: `Average: ${currency} ${paymentData?.outstandingsCarryforward?.averageAmount ?? 'NA'} per consumer`,
        },
      ],
    },

    {
      key: "installments",
      bg: "bg-green-50 border-green-200",
      iconBg: "bg-green-100",
      icon: <Calendar className="w-5 h-5 text-green-600" />,
      title: "Include Payment Installments",
      description:
        "Payment agreement installments will be included in the bill",
      checked: includeInstallments,
      onChange: setIncludeInstallments,

      inactiveMessage: {
        title: "Payment Installments Not Included",
        description:
          "Payment agreement installments will not be included in this billing run. Consumers with payment plans will not see installment amounts on their bills.",
      },

      stats: [
        {
          label: "Consumers with payment plan",
          value:
            paymentData?.includeInstallments?.consumersWithPaymentPlan ?? "NA",
          subText: `${paymentData?.includeInstallments?.totalConsumer ?? 'NA'}% of total consumers`,
        },
        {
          label: "Total installment value",
          value:
            paymentData?.includeInstallments?.totalInstallmentValue ?? "NA",
          subText: `Average: ${currency} ${paymentData?.includeInstallments?.averageAmount ?? 'NA'} per consumer`,
        },
      ],
    },
  ];

  // Last Bill Run Payment Summary Data
  const paymentSummary = [
    {
      label: "Consumers Billed",
      value: paymentData?.lastBillRunSummary?.consumersBilled ?? "NA",
      subtext: "Total consumers",
      icon: <Users className="w-6 h-6 text-purple-600" />
    },
    {
      label: "Billed Amount",
      value: paymentData?.lastBillRunSummary?.billedAmount ?? "NA",
      subtext: "Total invoiced",
      icon: <FileText className="w-6 h-6 text-blue-600" />
    },
    {
      label: "Payment Collected",
      value: paymentData?.lastBillRunSummary?.paymentCollected ?? "NA",
      subtext: `${
        paymentData?.lastBillRunSummary?.paymentCollected ?? "NA"
      }% collection rate`,
      icon: <CheckCircle className="w-6 h-6 text-green-600" />
    },
    {
      label: "Outstanding",
      value: paymentData?.lastBillRunSummary?.collectionRate ?? "NA",
      subtext: `${
        paymentData?.lastBillRunSummary?.paymentCollected ?? "NA"
      }% pending`,
      icon: <Wallet className="w-6 h-6 text-orange-600" />
    },
  ];

  // Define columns for GenericTable
  const columns: TableColumn<any>[] = [
    {
      key: "accountNo",
      header: "Account No",
      type: "text",
      sortable: false,
      width: "w-32",
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      type: "text",
      sortable: false,
      width: "w-48",
    },
    {
      key: "lastBillAmount",
      header: "Last Bill Amount",
      type: "text",
      sortable: false,
      width: "w-40",
    },
    {
      key: "lastBilledOn",
      header: "Last Billed On",
      type: "text",
      sortable: false,
      width: "w-36",
    },
    {
      key: "outstanding",
      header: "Outstanding",
      type: "custom",
      sortable: false,
      width: "w-36",
      render: (value: string) => (
        <span className="font-semibold text-red-600">{value}</span>
      ),
    },
    {
      key: "lastPaymentAmount",
      header: "Last Payment Amount",
      type: "text",
      sortable: false,
      width: "w-44",
    },
    {
      key: "lastPaymentOn",
      header: "Last Payment On",
      type: "text",
      sortable: false,
      width: "w-36",
    },
  ];

  const totalItems = outstandingAccountsData?.count || 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages,
      totalItems,
      itemsPerPage: pageSize,
      startItem: (page - 1) * pageSize + 1,
      endItem: Math.min(page * pageSize, totalItems),
    }),
    [page, totalPages, totalItems],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">Payment Details</h2>
        <p className="text-sm text-gray-600 mt-1">
          Review payment information and transaction history.
        </p>
      </div>

      {/* Last Bill Run Payment Summary */}
      <div className="bg-purple-50 rounded-lg p-6 border border-purple-100">
        <div className="flex items-center gap-2 mb-4">
          <div>
            <h3 className="font-semibold text-lg">
              Last Bill Run Payment Summary
            </h3>
            <p className="text-sm text-gray-600">
              Overview of the previous billing cycle performance
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {paymentSummary.map((item, index) => (
            <QuickStatsCard
              key={index}
              title={item.label}
              count={item.value as number | string}
              trendSummary={item.subtext}
              icon={item.icon}
            />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {billingOptions.map((option) => (
          <div key={option.key} className={`rounded-lg border ${option.bg}`}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${option.iconBg}`}>
                    {option.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{option.title}</h3>
                    <p className="text-sm text-gray-600">
                      {option.description}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Active</span>
                  <Switch
                    checked={option.checked}
                    onCheckedChange={option.onChange}
                  />
                </div>
              </div>

              {/* Conditional Content */}
              {option.checked ? (
                // Show stats if switch is ON
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {option.stats.map((stat, idx) => (
                    <QuickStatsCard
                      key={idx}
                      title={stat.label}
                      count={stat.value as number | string}
                      trendSummary={stat.subText}
                    />
                  ))}
                </div>
              ) : (
                // Show inactive message if switch is OFF
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-1" />
                    <div>
                      <p className="font-semibold text-sm">
                        {option.inactiveMessage.title}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {option.inactiveMessage.description}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Top Outstanding Accounts Table - Using GenericTable */}
      <div className="bg-white rounded-lg border p-6">
        <div className="mb-4">
          <h3 className="font-semibold text-lg">Top Outstanding Accounts</h3>
          <p className="text-sm text-gray-600">Highest unpaid balances</p>
        </div>

        <GenericTable
          data={outstandingAccountsData?.result || []}
          columns={columns}
          rowKey="accountNo"
          pagination={pagination}
          onPageChange={handlePageChange}
          emptyMessage="No outstanding accounts found"
        />
      </div>
    </div>
  );
};

export default PaymentDetailsTab;
